import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Badge
} from '@mui/material';
import {
  Psychology,
  Lightbulb,
  TrendingUp,
  School,
  AutoAwesome,
  Insights,
  Assessment,
  Rocket,
  Star,
  CheckCircle,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { InterviewSummary } from '../types';

interface IntelligentAgentProps {
  onTriggerInterview: () => void;
  onEnhancedQuery: (query: string, context: any) => void;
  currentKnowledge: InterviewSummary | null;
  recentQueries: string[];
  userRole: string;
}

interface KnowledgeGap {
  category: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  suggestedQuestions: string[];
}

interface PerformanceMetrics {
  querySuccessRate: number;
  averageResponseTime: number;
  knowledgeCoverage: number;
  userSatisfaction: number;
  learningProgress: number;
}

export function IntelligentAgent({ 
  onTriggerInterview, 
  onEnhancedQuery, 
  currentKnowledge, 
  recentQueries, 
  userRole 
}: IntelligentAgentProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [knowledgeGaps, setKnowledgeGaps] = useState<KnowledgeGap[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [proactiveInsights, setProactiveInsights] = useState<string[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  // Analyze current knowledge and identify gaps
  const analyzeKnowledgeGaps = useCallback(async () => {
    setIsAnalyzing(true);
    try {
      // Simulate intelligent analysis
      const gaps: KnowledgeGap[] = [];
      
      if (!currentKnowledge || currentKnowledge.tablesDiscovered.length < 5) {
        gaps.push({
          category: 'Core Business Tables',
          description: 'Need to understand main operational tables for better query accuracy',
          priority: 'high',
          suggestedQuestions: [
            'What are your main product categories?',
            'How do you track customer relationships?',
            'What inventory management tables do you use?'
          ]
        });
      }

      if (!currentKnowledge || currentKnowledge.businessRules.length < 3) {
        gaps.push({
          category: 'Business Logic',
          description: 'Understanding business rules will improve query relevance',
          priority: 'medium',
          suggestedQuestions: [
            'What are your pricing rules?',
            'How do you handle returns and refunds?',
            'What are your shipping policies?'
          ]
        });
      }

      if (!currentKnowledge || currentKnowledge.relationshipsLearned.length < 4) {
        gaps.push({
          category: 'Data Relationships',
          description: 'Table relationships are crucial for complex queries',
          priority: 'high',
          suggestedQuestions: [
            'How do orders connect to customers?',
            'What links products to inventory?',
            'How are payments tracked?'
          ]
        });
      }

      setKnowledgeGaps(gaps);

      // Generate proactive insights
      const insights = generateProactiveInsights(gaps, recentQueries, userRole);
      setProactiveInsights(insights);

      // Calculate performance metrics
      const metrics = calculatePerformanceMetrics(gaps, currentKnowledge);
      setPerformanceMetrics(metrics);

    } catch (error) {
      console.error('Failed to analyze knowledge gaps:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [currentKnowledge, recentQueries, userRole]);

  const generateProactiveInsights = (gaps: KnowledgeGap[], queries: string[], role: string): string[] => {
    const insights: string[] = [];
    
    if (gaps.length > 0) {
      insights.push(`I've identified ${gaps.length} knowledge gaps that could improve my ability to help you.`);
    }

    if (queries.length > 0) {
      const recentQuery = queries[queries.length - 1];
      if (recentQuery.toLowerCase().includes('revenue') || recentQuery.toLowerCase().includes('sales')) {
        insights.push('Based on your recent questions about sales, I can help you analyze revenue trends and performance metrics.');
      }
      if (recentQuery.toLowerCase().includes('inventory') || recentQuery.toLowerCase().includes('stock')) {
        insights.push('I notice you\'re interested in inventory. I can help track stock levels, reorder points, and supply chain insights.');
      }
    }

    if (role === 'CEO') {
      insights.push('As a CEO, you might be interested in high-level business intelligence and strategic insights.');
    }

    return insights;
  };

  const calculatePerformanceMetrics = (gaps: KnowledgeGap[], knowledge: InterviewSummary | null): PerformanceMetrics => {
    const baseSuccessRate = knowledge ? 85 : 60;
    const gapPenalty = gaps.length * 5;
    const knowledgeBonus = knowledge ? (knowledge.tablesDiscovered.length * 2) + (knowledge.businessRules.length * 3) : 0;
    
    return {
      querySuccessRate: Math.min(95, Math.max(60, baseSuccessRate - gapPenalty + knowledgeBonus)),
      averageResponseTime: knowledge ? 2.5 : 4.2,
      knowledgeCoverage: knowledge ? Math.min(100, (knowledge.tablesDiscovered.length / 8) * 100) : 0,
      userSatisfaction: knowledge ? 4.2 : 3.5,
      learningProgress: knowledge ? Math.min(100, (knowledge.totalInsights / 20) * 100) : 0
    };
  };

  useEffect(() => {
    analyzeKnowledgeGaps();
  }, [analyzeKnowledgeGaps]);

  const handleSmartQuestion = (question: string) => {
    const context = {
      knowledgeGaps: knowledgeGaps,
      currentKnowledge: currentKnowledge,
      userRole: userRole,
      recentQueries: recentQueries
    };
    onEnhancedQuery(question, context);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  if (isAnalyzing) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Psychology sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">AI Agent Analyzing...</Typography>
          </Box>
          <LinearProgress variant="indeterminate" sx={{ height: 4, borderRadius: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Analyzing your knowledge gaps and generating intelligent insights...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* AI Agent Header */}
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AutoAwesome sx={{ mr: 1, fontSize: '2rem' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Intelligent AI Agent
            </Typography>
            <Badge badgeContent="AI" color="secondary" sx={{ ml: 'auto' }}>
              <Rocket />
            </Badge>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            I'm continuously learning and optimizing to provide you with the best business insights.
          </Typography>
        </CardContent>
      </Card>

      {/* Performance Dashboard */}
      {performanceMetrics && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Assessment sx={{ mr: 1, color: 'primary.main' }} />
              Performance Dashboard
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                  {performanceMetrics.querySuccessRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Query Success Rate
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {performanceMetrics.knowledgeCoverage}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Knowledge Coverage
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  {performanceMetrics.userSatisfaction}/5
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User Satisfaction
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                  {performanceMetrics.learningProgress}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Learning Progress
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Proactive Insights */}
      {proactiveInsights.length > 0 && (
        <Card sx={{ mb: 2, backgroundColor: '#e3f2fd' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Insights sx={{ mr: 1, color: 'primary.main' }} />
              Proactive Insights
            </Typography>
            <List dense>
              {proactiveInsights.map((insight, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Lightbulb color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={insight} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Knowledge Gaps Analysis */}
      {knowledgeGaps.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <School sx={{ mr: 1, color: 'primary.main' }} />
                Knowledge Gaps Analysis
              </Typography>
              <IconButton onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                I've identified {knowledgeGaps.length} areas where I could improve my understanding of your business.
                {knowledgeGaps.some(g => g.priority === 'high') && 
                  ' Some gaps are high priority and could significantly improve my ability to help you.'
                }
              </Typography>
            </Alert>

            <Collapse in={showDetails}>
              <List>
                {knowledgeGaps.map((gap, index) => (
                  <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                      <Chip 
                        label={gap.priority.toUpperCase()} 
                        color={getPriorityColor(gap.priority) as any}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {gap.category}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {gap.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {gap.suggestedQuestions.map((question, qIndex) => (
                        <Chip
                          key={qIndex}
                          label={question}
                          variant="outlined"
                          size="small"
                          clickable
                          onClick={() => handleSmartQuestion(question)}
                          sx={{ maxWidth: 300 }}
                        />
                      ))}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Collapse>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={onTriggerInterview}
                startIcon={<Psychology />}
                sx={{ mr: 2 }}
              >
                Start Knowledge Interview
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Smart Suggestions */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
            Smart Suggestions
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="Show me revenue trends this quarter"
              variant="outlined"
              clickable
              onClick={() => handleSmartQuestion("Show me revenue trends this quarter")}
              icon={<Star />}
            />
            <Chip
              label="What are our top performing products?"
              variant="outlined"
              clickable
              onClick={() => handleSmartQuestion("What are our top performing products?")}
              icon={<TrendingUp />}
            />
            <Chip
              label="Analyze customer retention patterns"
              variant="outlined"
              clickable
              onClick={() => handleSmartQuestion("Analyze customer retention patterns")}
              icon={<Insights />}
            />
            <Chip
              label="Inventory optimization recommendations"
              variant="outlined"
              clickable
              onClick={() => handleSmartQuestion("Inventory optimization recommendations")}
              icon={<CheckCircle />}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
